import Dictionary from './dictionary';
import {
  MIN_AGE_TO_TRAVEL_ALONE,
  MIN_AGE_TO_ACCOMPANY_MINORS,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_GDS_LOCATOR,
} from './constants';
import { applyUtcFormatWithoutConverting, convertDateToMilliseconds } from './timeFormats';
import { getSessionStorageItem } from '../operations/sessionStorage/utils';

const guestRequieresFlightAndIsSelected = (guestFlights) => {
  const isChangeFlightFlow = getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS);
  const gdsLocatorsArray = getSessionStorageItem(SESSION_STORAGE_GDS_LOCATOR);

  // change fliths scenario
  if (isChangeFlightFlow && gdsLocatorsArray && guestFlights?.length > 0) {
    return gdsLocatorsArray?.some((gds) => gds === guestFlights[0].gdsLocator);
  }

  // adding new fligths scenario
  if (!isChangeFlightFlow && !guestFlights) {
    return true;
  }
  return false;
};
const getPassengerType = (renderContent, flightPassengerType) => {
  const contentMapping = {
    CHILD: [
      renderContent(
        Dictionary.airFlightPassengerTypeChild.key,
        Dictionary.airFlightPassengerTypeChild.defaultValue,
      ),
    ],
    INFANT: [
      renderContent(
        Dictionary.airFlightPassengerTypeInfantOnSeat.key,
        Dictionary.airFlightPassengerTypeInfantOnSeat.defaultValue,
      ),
      renderContent(
        Dictionary.airFlightPassengerTypeInfantOnLap.key,
        Dictionary.airFlightPassengerTypeInfantOnLap.defaultValue,
      ),
    ],
    default: renderContent(
      Dictionary.airFlightPassengerTypeAdult.key,
      Dictionary.airFlightPassengerTypeAdult.defaultValue,
    ),
  };

  return contentMapping[flightPassengerType] || contentMapping.default;
};

const flightPassengerGuestMap = (renderContent, guests) => {
  return guests?.map(
    ({ flightGuest: { age, firstName, lastName, guestFlights }, flightPassengerType, id }) => {
      const getCheckboxStatus = guestRequieresFlightAndIsSelected(guestFlights);
      const newObj = {
        name: `${firstName.toLowerCase()} ${lastName.toLowerCase()}`,
        age,
        id,
        flightPassengerType: getPassengerType(renderContent, flightPassengerType),
        allowOnLapOption: flightPassengerType === 'INFANT',
        requiresFlightTicket: getCheckboxStatus,
        selected: getCheckboxStatus,
      };
      return newObj;
    },
  );
};

const getAssignData = (flightProfileData) => {
  const { flightProfile } = flightProfileData;
  const { city: flightToAirportCity, code: flightToAirportCode } =
    flightProfile.nearestAirport.arrival;
  const { arrival, departure } = flightProfile.flightWindows;
  const arrivalAirportCode = flightToAirportCode;
  const { id, currency } = flightProfile; // TODO: Fetch ID from token.
  const flightSearchVariables = {
    arrivalAirportCode,
    bookingID: id,
    currency,
  };
  return {
    arrival,
    departure,
    flightSearchVariables,
    flightToAirportCity,
    flightToAirportCode,
  };
};
const asignData = (flightProfileData) => {
  return getAssignData(flightProfileData);
};

const profileData = (flightProfileData, setNearestAirport, setFlightDates, setFlightSearchData) => {
  if (flightProfileData.flightProfile) {
    const { arrival, departure, flightSearchVariables, flightToAirportCity, flightToAirportCode } =
      asignData(flightProfileData);
    setNearestAirport(`${flightToAirportCity} (${flightToAirportCode})`);
    setFlightDates({
      startDate: arrival.latestDate,
      endDate: departure.earliestDate,
    });
    setFlightSearchData(flightSearchVariables);
  }
};

const deepCopy = (obj) => JSON.parse(JSON.stringify(obj));
const searchFormMenuItems = (renderContent, guestInfo, oneWayEnabled = true) => {
  // dep object copy to avoid any undesired mutation before processing.
  const guestInfoCopy = deepCopy(guestInfo);
  const guestItems = flightPassengerGuestMap(renderContent, guestInfoCopy);
  const flighTypeSelector = {
    id: 'flight-type-selector',
    type: 'dropDown',
    disabled: false,
    items: [
      {
        name: renderContent(
          Dictionary.airFlightTypeRoundtrip.key,
          Dictionary.airFlightTypeRoundtrip.defaultValue,
        ),
        key: renderContent(
          Dictionary.airFlightTypeRoundtrip.key,
          Dictionary.airFlightTypeRoundtrip.defaultValue,
        ),
      },
      // {
      //   name: renderContent(
      //     Dictionary.airFlightTypeMultiCity.key,
      //     Dictionary.airFlightTypeMultiCity.defaultValue,
      //   ),
      //   key: renderContent(
      //     Dictionary.airFlightTypeMultiCity.key,
      //     Dictionary.airFlightTypeMultiCity.defaultValue,
      //   ),
      // },
    ],
  };

  if (oneWayEnabled) {
    flighTypeSelector.items.push({
      name: renderContent(
        Dictionary.airFlightTypeOneWay.key,
        Dictionary.airFlightTypeOneWay.defaultValue,
      ),
      key: renderContent(
        Dictionary.airFlightTypeOneWay.key,
        Dictionary.airFlightTypeOneWay.defaultValue,
      ),
    });
  }

  const guestSelector = {
    id: 'guest-selector',
    type: 'guestMenu',
    labelMenu: renderContent(
      Dictionary.airSearchMenuLabel.key,
      Dictionary.airSearchMenuLabel.defaultValue,
    ),
    labelCancel: renderContent(
      Dictionary.airCancelLabel.key,
      Dictionary.airCancelLabel.defaultValue,
    ),
    labelDone: renderContent(Dictionary.airDoneLabel.key, Dictionary.airDoneLabel.defaultValue),
    labelOnLapNote: renderContent(
      Dictionary.airOnLapNoteLabel.key,
      Dictionary.airOnLapNoteLabel.defaultValue,
    ),
    unaccompaniedMinorError: renderContent(
      Dictionary.airUnaccompaniedMinorError.key,
      Dictionary.airUnaccompaniedMinorError.defaultValue,
    ),
    minAgeToTravelAlone: MIN_AGE_TO_TRAVEL_ALONE,
    minAgeToAccompanyMinors: MIN_AGE_TO_ACCOMPANY_MINORS,
    items: guestItems,
  };

  return [flighTypeSelector, guestSelector];
};

const compareFlightDates = (flight1, flight2, shouldCompareDeparture, isEarliestComparison) => {
  const fieldPrefix = shouldCompareDeparture ? 'depart' : 'arrival';
  const dateFieldToCompare = `${fieldPrefix}Date`;
  const timeFieldToCompare = `${fieldPrefix}Time`;
  const dateInMilliseconds1 = convertDateToMilliseconds(
    applyUtcFormatWithoutConverting(
      `${flight1[dateFieldToCompare]} ${flight1[timeFieldToCompare]}`,
    ),
  );
  const dateInMilliseconds2 = convertDateToMilliseconds(
    applyUtcFormatWithoutConverting(
      `${flight2[dateFieldToCompare]} ${flight2[timeFieldToCompare]}`,
    ),
  );

  if (dateInMilliseconds1 < dateInMilliseconds2) return isEarliestComparison ? -1 : 1;
  if (dateInMilliseconds1 > dateInMilliseconds2) return isEarliestComparison ? 1 : -1;
  if (flight1.lowestAvailableFare.value < flight2.lowestAvailableFare.value) return -1;
  if (flight1.lowestAvailableFare.value > flight2.lowestAvailableFare.value) return 1;
  return -1;
};

const sortByFlightSearch = (flights, option) => {
  return [...flights].sort((flight1, flight2) => {
    switch (option.key) {
      case 'price':
        if (flight1.lowestAvailableFare.value < flight2.lowestAvailableFare.value) return -1;
        if (flight1.lowestAvailableFare.value > flight2.lowestAvailableFare.value) return 1;
        if (flight1.totalDurationMins < flight2.totalDurationMins) return -1;
        if (flight1.totalDurationMins > flight2.totalDurationMins) return 1;
        return flight1.lowestAvailableFare.value - flight2.lowestAvailableFare.value;
      case 'departure-earliest': {
        return compareFlightDates(flight1, flight2, true, true);
      }
      case 'departure-latest': {
        return compareFlightDates(flight1, flight2, true, false);
      }
      case 'arrival-earliest': {
        return compareFlightDates(flight1, flight2, false, true);
      }
      case 'arrival-latest': {
        return compareFlightDates(flight1, flight2, false, false);
      }
      case 'duration':
        if (flight1.totalDurationMins < flight2.totalDurationMins) return -1;
        if (flight1.totalDurationMins > flight2.totalDurationMins) return 1;
        if (flight1.lowestAvailableFare.value < flight2.lowestAvailableFare.value) return -1;
        if (flight1.lowestAvailableFare.value > flight2.lowestAvailableFare.value) return 1;
        return flight1.totalDurationMins - flight2.totalDurationMins;
      case 'stops':
        if (flight1.flightSegments.length < flight2.flightSegments.length) return -1;
        if (flight1.flightSegments.length > flight2.flightSegments.length) return 1;
        if (flight1.lowestAvailableFare.value < flight2.lowestAvailableFare.value) return -1;
        if (flight1.lowestAvailableFare.value > flight2.lowestAvailableFare.value) return 1;
        return flight1.flightSegments.length - flight2.flightSegments.length;
      default:
        return flight2.lowestAvailableFare.value - flight1.lowestAvailableFare.value;
    }
  });
};

export default {
  flightPassengerGuestMap,
  profileData,
  searchFormMenuItems,
  sortByFlightSearch,
};
